import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { apiPost } from '../lib/api';

interface PersonLike {
  id: string;
  name: string;
}

interface AddPersonRowProps<T extends PersonLike> {
  /** The current search text from the picker. */
  query: string;
  /** Every known person, so an existing name is never offered as new. */
  people: T[];
  /** Called only after the person genuinely exists on the server. */
  onCreated: (person: T) => void;
}

/**
 * "Add person: <name>" for a picker whose search matches nobody.
 *
 * Extracted because three pickers needed it - meeting attendees, series
 * default attendees, and document attendees - and three copies of the
 * duplicate-name check is exactly how they drift apart.
 *
 * Renders nothing unless the typed name is genuinely new, so a caller can
 * drop it into a list unconditionally.
 */
export function AddPersonRow<T extends PersonLike>({ query, people, onCreated }: AddPersonRowProps<T>) {
  const [creating, setCreating] = useState(false);
  const name = query.trim();

  // Compared against ALL people, not just the picker's filtered list: someone
  // already attending is filtered out of the options but must not therefore
  // look creatable.
  const exists = people.some((p) => p.name.trim().toLowerCase() === name.toLowerCase());
  if (!name || exists) return null;

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    try {
      const person = await apiPost<T>('/people', { name });
      onCreated(person);
    } catch (err) {
      // Deliberately no callback: handing back a person who was never created
      // would leave the caller attaching an id that does not exist.
      console.error('Failed to create person:', err);
    } finally {
      setCreating(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleCreate}
      disabled={creating}
      aria-label={`Add person: ${name}`}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-left border-t border-border hover:bg-bg-hover transition-colors disabled:opacity-50"
    >
      <UserPlus size={14} className="text-accent flex-shrink-0" />
      <span className="text-xs text-text-primary truncate">
        Add person: <strong>{name}</strong>
      </span>
    </button>
  );
}
